import { createSelector } from 'reselect';

import { appState, translationParser } from './utils';
import { translator } from 'mage-i18n';

import { PLATFORMS_TYPES } from '../constants';
import { getSelectedCountryKey, getSelectedPlatformKey, getCurrentRegion } from './data';

const tKeyPrefix = 'products-fe.components.platform_instructions';

export const _getPlatforms = createSelector(appState, ({ platforms = {} }) => platforms.elements);

export const platformStepInstructionsTranslator = platformId =>
  createSelector(translationParser, translationParser => {
    if (!platformId) {
      return [];
    }
    const translations = translationParser(`${tKeyPrefix}.${platformId}`) || [];
    return translationMapper(translations);
  });

export const translationMapper = translationsArray =>
  translationsArray.map(({ step_instructions: stepInstructions, ...step }) => ({
    ...step,
    stepInstructions: stepInstructions.map(instructionParts =>
      instructionParts.map(({ link = false, ...part }) => ({
        ...part,
        link,
      })),
    ),
  }));

export const getPlatforms = createSelector(_getPlatforms, translator, (platforms = [], t) =>
  platforms.map(({ requiresDevelopment, ...p }) => ({
    ...p,
    key: p.id,
    label: t(`products-fe.platforms.${p.id}`, {}, p.name),
  })),
);

export const getPlatformKeysThatRequireDevelopment = createSelector(_getPlatforms, platforms =>
  platforms.filter(p => p.requiresDevelopment).map(p => p.id),
);

const getPlatformsOfType = type =>
  createSelector(getPlatforms, (platforms = []) => platforms.filter(p => p.type === type));

export const getStandardPlatforms = createSelector(
  getPlatforms,
  getSelectedCountryKey,
  getCurrentRegion,
  (platforms = [], countryKey, region) => {
    // render US platforms as default for us region
    if (region === 'us' && !['US', 'CA', 'MX'].includes(countryKey)) {
      countryKey = 'US';
    }
    const platformsPerCountry = platforms.filter(({ standardOptionIn = [] }) =>
      standardOptionIn.includes(countryKey),
    );

    return platformsPerCountry;
  },
);
export const getSupportedPlatforms = getPlatformsOfType(PLATFORMS_TYPES.SUPPORTED);
export const getUnupportedPlatforms = getPlatformsOfType(PLATFORMS_TYPES.UNSUPPORTED);
export const getNativePlatforms = getPlatformsOfType(PLATFORMS_TYPES.NATIVE);
export const getPlatformsWithoutLink = getPlatformsOfType(PLATFORMS_TYPES.WITHOUT_LANDING_PAGE);
export const getManualContactPlatforms = getPlatformsOfType(PLATFORMS_TYPES.MANUAL_CONTACT);

const getKeysFromPlatforms = (platforms = []) => {
  return platforms.map(platform => platform.id);
};

export const getStandardPlatformKeys = createSelector(getStandardPlatforms, getKeysFromPlatforms);
export const getSupportedPlatformKeys = createSelector(getSupportedPlatforms, getKeysFromPlatforms);
export const getUnsupportedPlatformKeys = createSelector(
  getUnupportedPlatforms,
  getKeysFromPlatforms,
);
export const getNativePlatformKeys = createSelector(getNativePlatforms, getKeysFromPlatforms);
export const getNoLinkPlatformKeys = createSelector(getPlatformsWithoutLink, getKeysFromPlatforms);
export const getManualContactPlatformKeys = createSelector(
  getManualContactPlatforms,
  getKeysFromPlatforms,
);

export const getSelectedPlatform = createSelector(
  getSelectedPlatformKey,
  getPlatforms,
  (id, platforms) => platforms.find(p => p.id === id),
);

export const getPlatformOptions = createSelector(
  getPlatforms,
  getSelectedCountryKey,
  (platforms, countryKey) =>
    platforms.filter(
      ({ type, supportedCountries }) =>
        type &&
        type !== PLATFORMS_TYPES.OTHER &&
        supportedCountries &&
        supportedCountries.includes(countryKey),
    ),
);
